import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { saveQuestionnaireProgress } from '../utils/sessionUtils';

const QuestionnaireContext = createContext();

export const useQuestionnaire = () => {
  const context = useContext(QuestionnaireContext);
  if (!context) {
    throw new Error('useQuestionnaire must be used within a QuestionnaireProvider');
  }
  return context;
};

export const QuestionnaireProvider = ({ children }) => {
  const { user } = useAuth();
  const saveTimeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, []);

  // Step mapping for questionnaire flow
  const stepMapping = {
    // Service selection
    'service': (data) => ({
      serviceType: data.serviceType,
      serviceName: data.serviceName,
      cleaningOption: data.cleaningOption,
      extraServices: data.extraServices
    }),
    
    // Property details
    'property': (data) => ({
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      propertyType: data.propertyType,
      squareFootage: data.squareFootage,
      bedrooms: data.bedrooms,
      bathrooms: data.bathrooms,
      floors: data.floors,
      pets: data.pets,
      specialInstructions: data.specialInstructions
    }),
    
    // Scheduling
    'scheduling': (data) => ({
      serviceDate: data.serviceDate,
      serviceTime: data.serviceTime,
      frequency: data.frequency,
      flexibleDates: data.flexibleDates
    }),
    
    // Customer info
    'customer': (data) => ({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      zipCode: data.zipCode,
      contactPreference: data.contactPreference
    }),
    
    // Guest info (similar to customer but for non-account users)
    'guest': (data) => ({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      zipCode: data.zipCode,
      contactPreference: data.contactPreference
    }),
    
    // Payment selection
    'payment': (data) => ({
      method: data.method,
      voucherCode: data.voucherCode,
      estimatedPrice: data.estimatedPrice
    })
  };

  // Check if data has meaningful content
  const hasMeaningfulData = (stepKey, data) => {
    if (!data || typeof data !== 'object') return false;
    
    switch (stepKey) {
      case 'customer':
      case 'guest':
        return !!(data.firstName || data.lastName || data.email || data.phone);
      
      case 'property':
        return !!(data.address || data.city || data.zipCode || 
                 (data.propertyType && data.propertyType !== 'house'));
      
      case 'service':
        return !!(data.serviceType || data.cleaningOption || 
                 (data.extraServices && data.extraServices.length > 0));
      
      case 'scheduling':
        return !!(data.frequency || data.serviceDate || data.serviceTime || data.flexibleDates);
      
      case 'payment':
        return !!(data.method || data.voucherCode);
      
      default:
        return Object.keys(data).some(key => data[key] !== null && data[key] !== undefined && data[key] !== '');
    }
  };

  // Save questionnaire progress with debouncing
  const saveProgress = useCallback(async (stepKey, rawData, immediate = false) => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    const performSave = async () => {
      try {
        // Extract relevant data for this step
        const stepMapper = stepMapping[stepKey];
        const formData = stepMapper ? stepMapper(rawData) : rawData;

        // Only save if we have meaningful data
        if (!hasMeaningfulData(stepKey, formData)) {
          console.log('📋 Questionnaire save skipped - no meaningful data:', {
            step: stepKey,
            dataKeys: Object.keys(formData || {})
          });
          return false;
        }

        console.log('📋 Saving questionnaire progress:', {
          step: stepKey,
          userId: user?.id || 'anonymous',
          dataKeys: Object.keys(formData),
          immediate
        });

        const success = await saveQuestionnaireProgress(stepKey, formData, user?.id);
        
        if (success) {
          console.log('✅ Questionnaire progress saved:', stepKey);
        } else {
          console.warn('❌ Failed to save questionnaire progress:', stepKey);
        }
        
        return success;
      } catch (error) {
        console.error('❌ Error saving questionnaire progress:', {
          step: stepKey,
          error: error.message
        });
        return false;
      }
    };

    if (immediate) {
      return performSave();
    } else {
      // Debounce by 2 seconds for better UX
      saveTimeoutRef.current = setTimeout(performSave, 2000);
      return Promise.resolve(true);
    }
  }, [user, stepMapping]);

  // Save progress when user moves to next step (immediate save)
  const saveStepProgress = useCallback((stepKey, data) => {
    return saveProgress(stepKey, data, true);
  }, [saveProgress]);

  // Save progress during form input (debounced)
  const saveFormProgress = useCallback((stepKey, data) => {
    return saveProgress(stepKey, data, false);
  }, [saveProgress]);

  const value = {
    saveProgress,
    saveStepProgress,
    saveFormProgress
  };

  return (
    <QuestionnaireContext.Provider value={value}>
      {children}
    </QuestionnaireContext.Provider>
  );
};